import { CSSProperties, ReactElement, useCallback, useMemo } from 'react';

import CanvasDraw from 'react-canvas-draw';
import {
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';

import { WindowSize } from 'hooks/useResize';

import { CanvasDrawElement } from '../types';
import { BRUSH_COLOR, BRUSH_RADIUS, LAZY_RADIUS } from '../model';

interface PaintingCanvasProps {
  dimensions: WindowSize;
  canvasWidth: number;
  canvasHeight: number;
  isPainting: boolean;
  setCanvasRef: (instance: CanvasDrawElement | null) => void;
  src?: string;
  initialScale: number;
  minScale: number;
  maxScale: number;
}

const DOUBLE_CLICK_OPTION = { disabled: true } as const;
const INTERACTION_ENABLED = { disabled: false } as const;
const INTERACTION_DISABLED = { disabled: true } as const;

export function PaintingCanvas({
  dimensions,
  canvasWidth,
  canvasHeight,
  isPainting,
  setCanvasRef,
  src,
  initialScale,
  minScale,
  maxScale,
}: PaintingCanvasProps): ReactElement | null {
  const wrapperStyle = useMemo(
    (): CSSProperties => ({
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
    }),
    [dimensions.width, dimensions.height]
  );

  const transformKey = `${dimensions.width}x${dimensions.height}`;
  const interactionOption = isPainting
    ? INTERACTION_DISABLED
    : INTERACTION_ENABLED;

  const handleRef = useCallback(
    (instance: CanvasDrawElement | null) => {
      if (instance?.coordSystem) {
        const cs = instance.coordSystem;
        const original = cs.clientPointToViewPoint.bind(cs);
        cs.clientPointToViewPoint = (
          point: { clientX: number; clientY: number },
          view?: unknown
        ) => {
          const canvas = cs.canvas;
          if (!canvas) return original(point, view);
          const rect = canvas.getBoundingClientRect();
          if (!rect.width || !rect.height) return original(point, view);
          const cssScaleX = canvas.width / rect.width;
          const cssScaleY = canvas.height / rect.height;
          return original(
            {
              clientX: rect.left + (point.clientX - rect.left) * cssScaleX,
              clientY: rect.top + (point.clientY - rect.top) * cssScaleY,
            },
            view
          );
        };
      }
      setCanvasRef(instance);
    },
    [setCanvasRef]
  );

  if (!canvasWidth || !canvasHeight || !initialScale) {
    return null;
  }

  return (
    <TransformWrapper
      key={transformKey}
      initialScale={initialScale}
      minScale={minScale}
      maxScale={maxScale}
      centerOnInit
      limitToBounds
      centerZoomedOut
      disablePadding
      alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
      velocityAnimation={{ disabled: true }}
      doubleClick={DOUBLE_CLICK_OPTION}
      panning={interactionOption}
      pinch={interactionOption}
      wheel={interactionOption}
    >
      <TransformComponent wrapperStyle={wrapperStyle}>
        {/* @ts-ignore react 18 types */}
        <CanvasDraw
          ref={handleRef}
          brushRadius={BRUSH_RADIUS}
          brushColor={BRUSH_COLOR}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          imgSrc={src}
          lazyRadius={LAZY_RADIUS}
          disabled={!isPainting}
        />
      </TransformComponent>
    </TransformWrapper>
  );
}
